import React, { useEffect, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import styles from "./EnergyPurchaseDetailPop.module.scss";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { energySlice } from "../../../lib/store/energy/slice";
import { useLanguage } from "../../../hooks/useLanguage";

// Eye icons (no external dependency)
const EyeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ display: "block" }}
  >
    <path
      d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z"
      stroke="currentColor"
      strokeWidth="2"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ display: "block" }}
  >
    <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" />
    <path
      d="M10.6 10.6A3 3 0 0 0 12 15a3 3 0 0 0 2.4-4.4"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path
      d="M1 12s4-7 11-7c2.1 0 4 .6 5.6 1.5M23 12s-4 7-11 7c-2.1 0-4-.6-5.6-1.5"
      stroke="currentColor"
      strokeWidth="2"
    />
  </svg>
);

const EnergyPurposeHistoryDetailPop = ({ show, onHide }) => {
  const t = useLanguage();
  const dispatch = useDispatch();

  const [showFull, setShowFull] = useState(false);

  const { energyPurchaseDetail, loadingPurchaseDetail } = useSelector(
    ({ Energy }) => Energy,
  );

  // Clear detail data on popup unmount
  // useEffect(() => {
  //   return () => {
  //     dispatch(energySlice.actions.setEnergyPurchaseDetail(null));
  //   };
  // }, [dispatch]);

  const onCloseHandler = () => {
    onHide();
  };

  const getStatusUI = (status = "") => {
    const normalized = status.toLowerCase();

    switch (normalized) {
      case "success":
        return { color: "#28a745", icon: "✔" };
      case "failure":
        return { color: "#dc3545", icon: "✖" };
      case "cancelled":
        return { color: "#ffc107", icon: "⛔" };
      case "expired":
        return { color: "#fd7e14", icon: "⏰" };
      case "processing":
        return { color: "#0d6efd", icon: "⏳" }; // blue
      default:
        return { color: "#6f42c1", icon: "●" }; // purple
    }
  };

  return (
    <Modal
      show={show}
      onHide={onCloseHandler}
      backdrop={true}
      centered
      scrollable
      keyboard={false}
      className={`${styles.gameHistoryDetail} bottomPop`}
    >
      <div className="modalWrpper">
        <Modal.Body>
          {/* Close Button */}
          <Button
            onClick={onCloseHandler}
            variant="transparent"
            className="border-0 p-0 position-absolute"
            style={{ top: 15, right: 15 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="26"
              height="26"
              viewBox="0 0 26 26"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M13.0024 26.0048C20.1834 26.0048 26.0048 20.1834 26.0048 13.0024C26.0048 5.82137 20.1834 0 13.0024 0C5.82137 0 0 5.82137 0 13.0024C0 20.1834 5.82137 26.0048 13.0024 26.0048ZM13.0062 14.1485L18.1747 19.3251L19.3205 18.1711L14.152 13.0026L19.3205 7.83412L18.1747 6.68827L13.0062 11.8568L7.83767 6.68015L6.6837 7.83412L11.8603 13.0026L6.6837 18.1711L7.83767 19.3251L13.0062 14.1485Z"
                fill="#506380"
              />
            </svg>
          </Button>

          {/* Loader */}
          {loadingPurchaseDetail && (
            <div className="d-flex justify-content-center align-items-center py-4">
              <Spinner className={styles.mSpinner} size="sm" />
            </div>
          )}
          <div className="text-center">
            <h5 className="m-0 themeClr2 fw-bold">Energy Purchase Details</h5>
          </div>

          {/* Detail Content */}
          {!loadingPurchaseDetail && energyPurchaseDetail && (
            <div
              className="mx-auto pt-4 allow-scroll"
              style={{
                maxWidth: 292,
                maxHeight: "calc(100vh - 230px)",
                overflowY: "auto",
                fontSize: "15px", // 👈 reduce font size here
                lineHeight: "2.0", // 👈 optional: better readability
              }}
            >
              <ul className="list-unstyled ps-0 mb-0">
                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Energy</span>
                  <span>{energyPurchaseDetail.energy}</span>
                </li>

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Amount</span>
                  <span>
                    {Number(energyPurchaseDetail?.amount ?? 0).toFixed(2)}{" "}
                    {(
                      energyPurchaseDetail?.currencySymbol ?? ""
                    ).toUpperCase() || "--"}
                  </span>
                </li>

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Status</span>
                  {(() => {
                    const status = energyPurchaseDetail.paymentStatus || "";
                    const { color, icon } = getStatusUI(status);
                    const label =
                      status.charAt(0).toUpperCase() + status.slice(1);

                    return (
                      <span style={{ color, fontWeight: 600 }}>
                        {icon} {label}
                      </span>
                    );
                  })()}
                </li>

                {["cancelled", "failed", "expired"].includes(
                  (energyPurchaseDetail?.order_status || "").toLowerCase(),
                ) && (
                  <li
                    className="text-white py-2 my-2"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      borderBottom: "1px dashed #313131",
                    }}
                  >
                    {/* Left label */}
                    <span style={{ maxWidth: "45%" }}>Message</span>

                    {/* Right content */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        maxWidth: "55%",
                        minWidth: 0, // 🔑 required for ellipsis
                      }}
                    >
                      <span
                        title={energyPurchaseDetail?.message}
                        style={
                          showFull
                            ? {
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                              }
                            : {
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0,
                              }
                        }
                      >
                        {energyPurchaseDetail?.message ?? "--"}
                      </span>

                      {energyPurchaseDetail?.message &&
                        energyPurchaseDetail.message.length > 50 && (
                          <span
                            onClick={() => setShowFull(!showFull)}
                            style={{
                              cursor: "pointer",
                              color: "#ccc",
                              flexShrink: 0, // 🔑 icon never moves
                            }}
                          >
                            {showFull ? <EyeSlashIcon /> : <EyeIcon />}
                          </span>
                        )}
                    </div>
                  </li>
                )}

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Energy Package</span>
                  <span>
                    {energyPurchaseDetail?.energyPackageDetails
                      ? energyPurchaseDetail?.energyPackageDetails?.name
                      : "Custom"}
                  </span>
                </li>

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Previous Energy Balance</span>
                  <span>{energyPurchaseDetail.prevEnergyBalance}</span>
                </li>

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>New Energy Balance</span>
                  <span>{energyPurchaseDetail.newEnergyBalance}</span>
                </li>

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Payment Type</span>
                  <span>
                    {energyPurchaseDetail.paymentType
                      ? energyPurchaseDetail.paymentType
                          .charAt(0)
                          .toUpperCase() +
                        energyPurchaseDetail.paymentType.slice(1)
                      : ""}
                  </span>
                </li>
                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Date</span>
                  <span>
                    {moment(energyPurchaseDetail.date_created_utc).format(
                      "DD.MM.YY HH:mm:ss",
                    )}
                  </span>
                </li>
                {energyPurchaseDetail.paymentType !== "crypto" && (
                  <>
                    <li
                      className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                      style={{ borderBottom: "1px dashed #313131" }}
                    >
                      <span>Payment Method</span>
                      <span>
                        {energyPurchaseDetail.payment_method
                          ? energyPurchaseDetail.payment_method
                              .charAt(0)
                              .toUpperCase() +
                            energyPurchaseDetail.payment_method.slice(1)
                          : ""}
                      </span>
                    </li>
                    <li
                      className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                      style={{ borderBottom: "1px dashed #313131" }}
                    >
                      <span>Order ID</span>
                      <span>{energyPurchaseDetail.order_id ?? "--"}</span>
                    </li>
                    <li
                      className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                      style={{ borderBottom: "1px dashed #313131" }}
                    >
                      <span>Order Status</span>
                      <span>
                        {energyPurchaseDetail.order_status
                          ? energyPurchaseDetail.order_status
                              .charAt(0)
                              .toUpperCase() +
                            energyPurchaseDetail.order_status.slice(1)
                          : ""}
                      </span>
                    </li>
                  </>
                )}

                <li
                  className="text-white py-2 my-2 d-flex justify-content-between gap-10"
                  style={{ borderBottom: "1px dashed #313131" }}
                >
                  <span>Completed At</span>
                  <span>
                    {energyPurchaseDetail.transactionCompletedAt
                      ? moment(
                          energyPurchaseDetail.transactionCompletedAt,
                        ).format("DD.MM.YY HH:mm:ss")
                      : "--"}
                  </span>
                </li>
              </ul>
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default EnergyPurposeHistoryDetailPop;
